import useCopyToClipboard from "@/hooks/useCopyToClipboard";
import { useCallback, useEffect, useRef, useState } from "react";

export type UseMarqueeCopyReturn = {
  showCopied: boolean;
  textRef: React.RefObject<HTMLSpanElement | null>;
  containerRef: React.RefObject<HTMLSpanElement | null>;
  startMarquee: () => void;
  stopMarquee: () => void;
  handleCopy: (value: string) => void;
};

/** Encapsulates marquee-on-hover + copy-to-clipboard with a brief "copied" overlay. */
export function useMarqueeCopy(): UseMarqueeCopyReturn {
  const [, setCopiedText] = useCopyToClipboard();
  const [showCopied, setShowCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLSpanElement>(null);
  const animationRef = useRef<Animation | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      animationRef.current?.cancel();
    };
  }, []);

  const startMarquee = useCallback(() => {
    const textEl = textRef.current;
    const containerEl = containerRef.current;
    if (!textEl || !containerEl) return;
    animationRef.current?.cancel();
    const overflow = textEl.scrollWidth - containerEl.clientWidth;
    if (overflow <= 0) return;
    const scrollMs = Math.max(500, (overflow / 80) * 1000);
    const pauseMs = 500;
    const totalMs = (scrollMs + pauseMs) * 2;
    const p1 = pauseMs / totalMs;
    const p2 = (pauseMs + scrollMs) / totalMs;
    const p3 = (pauseMs * 2 + scrollMs) / totalMs;
    animationRef.current = textEl.animate(
      [
        { transform: "translateX(0)", offset: 0 },
        { transform: "translateX(0)", offset: p1, easing: "ease-in-out" },
        { transform: `translateX(-${overflow}px)`, offset: p2 },
        {
          transform: `translateX(-${overflow}px)`,
          offset: p3,
          easing: "ease-in-out",
        },
        { transform: "translateX(0)", offset: 1 },
      ],
      { duration: totalMs, iterations: Infinity }
    );
  }, []);

  const stopMarquee = useCallback(() => {
    animationRef.current?.cancel();
    animationRef.current = null;
  }, []);

  const handleCopy = useCallback(
    (value: string) => {
      setCopiedText(value);
      setShowCopied(true);
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setShowCopied(false), 1500);
    },
    [setCopiedText]
  );

  return { showCopied, textRef, containerRef, startMarquee, stopMarquee, handleCopy };
}
